import {
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from "react";
import classNames from "classnames";
import {
  ARROW_DOWN_KEY,
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  ARROW_UP_KEY,
  ENTER_KEY,
  ESCAPE_KEY,
  SPACE_KEY,
} from "../../../../core/constants/keyboard_keys";
import Icon from "../../Icon";
import {
  COLUMN_WIDTH_DESKTOP,
  COLUMN_WIDTH_TOUCH,
  DEFAULT_MAX_HOURS,
  DEFAULT_MAX_MINUTES,
  DEFAULT_MAX_SECONDS,
  DEFAULT_SEGMENTS,
  DEFAULT_UNIT_LABELS,
  DEFAULT_VISIBLE_ITEMS,
  ITEM_HEIGHT_DESKTOP,
  ITEM_HEIGHT_TOUCH,
  MAX_SEGMENT_VALUE,
  SEGMENT_ORDER,
} from "./constants";
import TimePickerPopup, { TimePickerPopupHandle } from "./TimePickerPopup";
import { buildOptions, clamp } from "./timeValueHelper";
import { CustomTimePickerProps } from "./types";
import useScrollMode from "./useScrollMode";
import useTimePickerPopup from "./useTimePickerPopup";
import { getFocusable } from "../../../utils/useFocusTrap";
import useTimeValue from "./useTimeValue";

import inputStyles from "../../Inputs/Input.module.css";
import styles from "./CustomTimePicker.module.css";

/* Public surface of the component, so consumers only need this one import path. */
export type {
  CustomTimePickerProps,
  CustomTimeValue,
  PopupLayout,
  SegmentValues,
  TimeInteractionMode,
  TimeSegmentKey,
} from "./types";
/* Converters between the `value` prop / `onChange` payload and plain values. */
export {
  timeValueFromMilliseconds,
  timeValueFromString,
} from "./timeValueHelper";

/**
 * Reusable time picker built on top of the project input styles.
 *
 * - time is typed from the keyboard on desktop and picked by scrolling on mobile;
 * - the dropdown columns are always scrollable and shrink to fit small screens;
 * - `onChange` returns the value in several formats at once: a ":" separated
 *   string and the same time converted to milliseconds/seconds (a duration,
 *   not a timestamp).
 *
 * The entered value lives in `useTimeValue`, the dropdown in `useTimePickerPopup`.
 */
export const CustomTimePicker = ({
  id,
  inputRef: externalInputRef,
  label = "",
  hideLabel = false,
  placeholder,
  required = false,
  disabled = false,
  description = "",
  validationError = undefined,
  withValidationMessage = true,
  invalid = false,
  value,
  onChange,
  onBlur,
  onOpenChange,
  segments = DEFAULT_SEGMENTS,
  maxHours = DEFAULT_MAX_HOURS,
  maxMinutes = DEFAULT_MAX_MINUTES,
  maxSeconds = DEFAULT_MAX_SECONDS,
  hourStep = 1,
  minuteStep = 1,
  secondStep = 1,
  visibleItems = DEFAULT_VISIBLE_ITEMS,
  interactionMode = "auto",
  unitLabels,
  doneLabel = "Done",
  showActions,
  width,
  className,
  inputClassName,
  popupClassName,
  dataCy,
  dataCyBtn,
  ariaLabel,
}: CustomTimePickerProps) => {
  /* One generated id per instance, reused for every part that needs one. */
  const instanceId = useId();
  const inputId = id ?? instanceId;
  const popupId = `ctp-popup-${instanceId}`;
  const errorId = `ctp-error-${instanceId}`;
  const descriptionId = `ctp-description-${instanceId}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popupHandleRef = useRef<TimePickerPopupHandle>(null);

  /* derived configuration */

  const activeSegments = useMemo(() => {
    const filtered = SEGMENT_ORDER.filter((key) => segments.includes(key));
    return filtered.length ? filtered : SEGMENT_ORDER;
  }, [segments]);

  const maxima = useMemo(
    () =>
      activeSegments.map((key) => {
        if (key === "hours") return clamp(maxHours, 0, MAX_SEGMENT_VALUE);
        if (key === "minutes") return clamp(maxMinutes, 0, DEFAULT_MAX_MINUTES);
        return clamp(maxSeconds, 0, DEFAULT_MAX_SECONDS);
      }),
    [activeSegments, maxHours, maxMinutes, maxSeconds],
  );

  const optionsPerSegment = useMemo(
    () =>
      activeSegments.map((key, index) => {
        let step = secondStep;
        if (key === "hours") step = hourStep;
        if (key === "minutes") step = minuteStep;
        return buildOptions(maxima[index], Math.max(1, Math.floor(step)));
      }),
    [activeSegments, maxima, hourStep, minuteStep, secondStep],
  );

  const resolvedUnitLabels = useMemo(
    () => ({ ...DEFAULT_UNIT_LABELS, ...unitLabels }),
    [unitLabels],
  );

  const resolvedPlaceholder =
    placeholder ??
    activeSegments.map((key) => resolvedUnitLabels[key]).join(":");
  const resolvedAriaLabel = ariaLabel || label || resolvedPlaceholder;

  const showValidationError = !!validationError && withValidationMessage;
  // associates the error and the hint with the input, so both are announced
  const describedBy =
    [showValidationError ? errorId : "", description ? descriptionId : ""]
      .filter(Boolean)
      .join(" ") || undefined;

  const scrollMode = useScrollMode(interactionMode);
  const withPopup = scrollMode;
  const withActions = showActions ?? scrollMode;
  const itemHeight = scrollMode ? ITEM_HEIGHT_TOUCH : ITEM_HEIGHT_DESKTOP;
  const columnWidth = scrollMode ? COLUMN_WIDTH_TOUCH : COLUMN_WIDTH_DESKTOP;

  /* #endregion */

  const { isOpen, layout, openPopup, closePopup, autoFocusPopup, popupRef } =
    useTimePickerPopup({
      disabled,
      segmentCount: activeSegments.length,
      itemHeight,
      columnWidth,
      visibleItems,
      withActions,
      anchorRef: inputRowRef,
      containerRef,
      inputRef,
      onOpenChange,
    });

  // the viewport can cross the breakpoint while the dropdown is open
  useEffect(() => {
    if (isOpen && !withPopup) closePopup(false);
  }, [isOpen, withPopup, closePopup]);

  /** The dropdown is portaled away, but it is still a part of this widget. */
  const isInternalFocusTarget = useCallback(
    (node: Node | null) =>
      !!node &&
      (!!containerRef.current?.contains(node) ||
        !!popupRef.current?.contains(node)),
    [popupRef],
  );

  const {
    text,
    segmentValues,
    setSegmentValue,
    handleInputChange,
    handleInputBlur,
    handleTypingKeyDown,
    stepCaretSegment,
    moveCaretSegment,
    clearCaretSegment,
  } = useTimeValue({
    value,
    onChange,
    onBlur,
    segments: activeSegments,
    maxima,
    optionsPerSegment,
    inputRef,
    isInternalFocusTarget,
  });

  /* #region input handlers */

  const registerInput = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof externalInputRef === "function") {
        externalInputRef(node);
      } else if (externalInputRef) {
        (
          externalInputRef as MutableRefObject<HTMLInputElement | null>
        ).current = node;
      }
    },
    [externalInputRef],
  );

  /** Opens the dropdown without stealing focus, for pointer interactions. */
  const openFromPointer = useCallback(() => {
    if (withPopup) openPopup();
  }, [withPopup, openPopup]);

  /**
   * Moves focus into the dropdown, opening it first if needed. The popup lives
   * in a portal, so this is the only way a keyboard user can reach its options.
   */
  const focusPopup = useCallback(() => {
    if (isOpen) popupHandleRef.current?.focusFirstColumn();
    else openPopup(true);
  }, [isOpen, openPopup]);

  /* Confirming from inside the popup always hands focus back to the input. */
  const closeFromPopup = useCallback(() => closePopup(true), [closePopup]);

  const handleInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // digits overwrite the segment under the caret, never insert into the text
    if (!scrollMode && handleTypingKeyDown(e)) return;

    switch (e.key) {
      case ARROW_DOWN_KEY:
        e.preventDefault();
        // Alt+ArrowDown opens the dropdown, focus stays here for typing
        if (!e.altKey) stepCaretSegment(-1);
        else if (withPopup) openPopup();
        break;
      case ARROW_UP_KEY:
        e.preventDefault();
        if (!e.altKey) stepCaretSegment(1);
        else if (withPopup) closePopup();
        break;
      case ARROW_LEFT_KEY:
      case ARROW_RIGHT_KEY:
        // whole segments, so arrows keep operating on the same unit
        e.preventDefault();
        moveCaretSegment(e.key === ARROW_LEFT_KEY ? -1 : 1);
        break;
      case SPACE_KEY:
        // resets the segment instead of letting the browser replace its digits
        if (scrollMode) break;
        e.preventDefault();
        clearCaretSegment();
        break;
      case ENTER_KEY:
        if (!withPopup) break;
        // never submit the surrounding form while picking a time
        e.preventDefault();
        // Alt+Enter hands focus to the dropdown, plain Enter only opens it
        if (e.altKey) focusPopup();
        else openPopup();
        break;
      case ESCAPE_KEY:
        // dismisses the dropdown; pressed inside it, the popup handles it itself
        if (!isOpen) break;
        e.preventDefault();
        closePopup();
        break;
      default:
        // Tab is left alone: focus moves on and the blur below hides the popup
        break;
    }
  };

  /**
   * Tab reached an edge of the dropdown. Instead of trapping focus, hand it to
   * the form: forward goes to whatever follows this field, backward returns to
   * the input. Without this the browser would wrap to the top of the document,
   * because the popup is portaled to the end of <body>.
   */
  const handleTabOut = useCallback(
    (forward: boolean) => {
      const input = inputRef.current;
      closePopup(false);

      if (!forward) {
        input?.focus();
        return;
      }

      const candidates = getFocusable(document.body).filter(
        (el) => !popupRef.current?.contains(el),
      );
      const index = input ? candidates.indexOf(input) : -1;
      if (index < 0) {
        input?.focus();
        return;
      }
      candidates[index + 1]?.focus();
    },
    [closePopup, popupRef],
  );

  /**
   * Hide the dropdown once focus leaves the widget for good. Moving focus
   * between the input and the popup does not count, even though the popup is
   * portaled away - React routes its focus events through this container.
   */
  const handleContainerBlur = (e: ReactFocusEvent<HTMLDivElement>) => {
    if (!isOpen) return;
    if (isInternalFocusTarget(e.relatedTarget as Node | null)) return;
    // focus already moved on, do not drag it back
    closePopup(false);
  };

  /* #endregion */

  return (
    <div
      ref={containerRef}
      className={classNames(
        inputStyles.inputContainer,
        styles.container,
        className,
      )}
      onBlur={handleContainerBlur}
    >
      {/* label row */}
      <div
        className={classNames({
          [inputStyles.labelRow]: !hideLabel && !!label,
          visuallyHidden: hideLabel || !label,
        })}
      >
        {required && <span className={inputStyles.requiredIcon}>*</span>}
        {label && <label htmlFor={inputId}>{label}</label>}
      </div>

      {/* input row */}
      <div
        ref={inputRowRef}
        className={classNames(
          inputStyles.inputRow,
          inputStyles.inputRowIconLeft,
          {
            [inputStyles.inputRowIconRight]: withPopup,
            [inputStyles.filledBorder]: !!text,
          },
        )}
        style={{ width }}
      >
        <span
          className={classNames(
            inputStyles.inputIcon,
            inputStyles.inputIconLeft,
          )}
          aria-hidden="true"
        >
          <Icon name="clock" size={24} color="grey" />
        </span>

        <input
          ref={registerInput}
          id={inputId}
          type="text"
          aria-label={resolvedAriaLabel}
          role={withPopup ? "combobox" : undefined}
          aria-haspopup={withPopup ? "dialog" : undefined}
          aria-expanded={withPopup ? isOpen : undefined}
          aria-controls={withPopup && isOpen ? popupId : undefined}
          aria-keyshortcuts={withPopup ? "Alt+Enter" : undefined}
          aria-required={required}
          aria-invalid={invalid || !!validationError}
          aria-describedby={describedBy}
          autoComplete="off"
          // on touch devices the value is scrolled, so no on screen keyboard
          inputMode={scrollMode ? "none" : "numeric"}
          readOnly={scrollMode}
          disabled={disabled}
          placeholder={resolvedPlaceholder}
          value={text}
          data-cy={dataCy}
          className={classNames(inputClassName, styles.input, {
            [inputStyles.inputValidationError]: invalid || !!validationError,
            [styles.inputReadOnly]: scrollMode,
          })}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          onClick={openFromPointer}
        />

        {/* purely decorative: the dropdown is opened by clicking the input */}
        {withPopup && (
          <span
            data-cy={dataCyBtn}
            className={classNames(
              inputStyles.inputIcon,
              inputStyles.inputIconRight,
            )}
            aria-hidden="true"
          >
            <Icon
              name={isOpen ? "chevron-up" : "chevron-down"}
              size={16}
              color="grey"
            />
          </span>
        )}
      </div>

      {/* other */}
      {showValidationError ? (
        <div
          id={errorId}
          role="alert"
          data-cy="validation_error"
          className={inputStyles.validationError}
        >
          <Icon name="exclamation-circle" size={14} />
          {validationError}
        </div>
      ) : null}
      {description ? (
        <p id={descriptionId} className="description">
          {description}
        </p>
      ) : null}

      {withPopup && isOpen && (
        <TimePickerPopup
          ref={popupHandleRef}
          popupRef={popupRef}
          onTabOut={handleTabOut}
          autoFocus={autoFocusPopup}
          id={popupId}
          ariaLabel={resolvedAriaLabel}
          className={popupClassName}
          layout={layout}
          segments={activeSegments}
          unitLabels={resolvedUnitLabels}
          optionsPerSegment={optionsPerSegment}
          segmentValues={segmentValues}
          itemHeight={itemHeight}
          columnWidth={columnWidth}
          visibleItems={visibleItems}
          scrollMode={scrollMode}
          disabled={disabled}
          withActions={withActions}
          doneLabel={doneLabel}
          onSelect={setSegmentValue}
          onClose={closeFromPopup}
        />
      )}
    </div>
  );
};

export default CustomTimePicker;
